import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Loader2, CheckCircle2, ArrowLeft, Shield, Plane, AlertCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * ForgotPassword — shared between `admin` and `agent`.
 *
 * Agent variant uses the travel theme (navy → teal gradient, Plane icon).
 * Admin variant uses the neutral security theme (slate, Shield icon).
 * Both always return the same success UI regardless of whether the email
 * exists (backend's no-enumeration contract). Rate-limited at 5/min.
 */
const ForgotPassword = ({ userType = 'agent' }) => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [err, setErr] = useState('');

  const isAdmin = userType === 'admin';
  const endpoint = isAdmin ? '/api/admin/forgot-password' : '/api/agent/forgot-password';
  const loginPath = isAdmin ? '/admin/login' : '/agent/login';
  const roleLabel = isAdmin ? 'Admin' : 'Agent';
  const Icon = isAdmin ? Shield : Plane;

  // Agent = travel-themed card on travel-bg; Admin = clean slate on neutral bg
  const cfg = isAdmin
    ? {
        pageWrap: 'min-h-screen bg-gradient-to-br from-slate-50 to-gray-100 flex items-center justify-center p-4 py-12',
        iconWrap: 'w-16 h-16 bg-gradient-to-br from-slate-700 to-slate-900 rounded-2xl flex items-center justify-center mx-auto mb-4',
        title: 'text-[#0F172A]',
        focus: 'focus:ring-slate-400/30 focus:border-slate-600',
        link: 'text-slate-700 hover:text-slate-900',
        button: 'w-full py-4 bg-gradient-to-r from-slate-800 to-slate-900 text-white rounded-xl font-bold text-lg shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none',
      }
    : {
        pageWrap: 'min-h-screen travel-bg flex items-center justify-center p-4 py-12',
        iconWrap: 'w-16 h-16 bg-gradient-to-br from-[#1B375B] to-[#659A9B] rounded-2xl flex items-center justify-center mx-auto mb-4',
        title: 'text-[#1B375B]',
        focus: 'focus:ring-[#659A9B]/30 focus:border-[#659A9B]',
        link: 'text-[#659A9B] hover:text-[#1B375B]',
        button: 'w-full py-4 bg-gradient-to-r from-[#1B375B] to-[#335E77] text-white rounded-xl font-bold text-lg shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none',
      };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErr('');
    if (!email || !/\S+@\S+\.\S+/.test(email)) {
      setErr('Please enter a valid email address');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${BACKEND_URL}${endpoint}`, { email });
      setSubmitted(true);
    } catch (e2) {
      const detail = e2?.response?.data?.detail;
      if (e2?.response?.status === 429) {
        setErr('Too many requests. Please wait a minute and try again.');
      } else {
        setErr(typeof detail === 'string' ? detail : 'Unable to send reset email. Please try again.');
      }
    }
    setLoading(false);
  };

  return (
    <div className={cfg.pageWrap} data-testid={`${userType}-forgot-password-page`}>
      <div className="w-full max-w-md">
        <button
          onClick={() => navigate(loginPath)}
          className={`mb-6 flex items-center gap-2 text-gray-600 transition-colors ${isAdmin ? 'hover:text-slate-900' : 'hover:text-[#1B375B]'}`}
        >
          <ArrowLeft className="w-5 h-5" />
          <span className="text-sm font-medium">Back to Login</span>
        </button>

        <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-10">
          <div className="text-center mb-8">
            <div className={cfg.iconWrap}>
              <Icon className="w-8 h-8 text-white" />
            </div>
            <h1 className={`text-3xl font-bold mb-1 ${cfg.title}`}>Reset your password</h1>
            <p className="text-gray-500 text-sm">
              Enter your {roleLabel.toLowerCase()} account email — we'll send you a secure reset link.
            </p>
          </div>

          {submitted ? (
            <div data-testid={`${userType}-forgot-password-success`}>
              <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-5 flex gap-3">
                <CheckCircle2 className="w-6 h-6 text-emerald-600 flex-shrink-0 mt-0.5" />
                <div>
                  <div className="font-semibold text-emerald-900">Check your email</div>
                  <div className="text-sm text-emerald-800 mt-1">
                    If a {roleLabel.toLowerCase()} account exists for <b>{email}</b>, a password-reset link has been sent. The link expires in 1 hour.
                  </div>
                  <div className="text-xs text-emerald-700 mt-3">
                    Didn't receive it? Check your spam folder, or try again in a minute.
                  </div>
                </div>
              </div>
              <button
                onClick={() => navigate(loginPath)}
                data-testid={`${userType}-back-to-login`}
                className={cfg.button + ' mt-6'}
              >
                Back to login
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Email address</label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                  <input
                    type="email"
                    autoFocus
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={isAdmin ? 'admin@acftourism.com' : 'agent@example.com'}
                    data-testid={`${userType}-forgot-email-input`}
                    className={`w-full pl-12 pr-4 py-3.5 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 ${cfg.focus} transition-all`}
                  />
                </div>
              </div>

              {err && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-start gap-2" data-testid={`${userType}-forgot-error`}>
                  <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-red-700">{err}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                data-testid={`${userType}-forgot-submit`}
                className={cfg.button}
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Sending…
                  </span>
                ) : 'Send reset link'}
              </button>

              <p className="text-center text-sm text-gray-600">
                Remembered it?{' '}
                <button
                  type="button"
                  onClick={() => navigate(loginPath)}
                  className={`font-semibold ${cfg.link} transition-colors`}
                >
                  Back to login
                </button>
              </p>
            </form>
          )}
        </div>

        <div className="text-center mt-6">
          <p className="text-xs text-gray-500">Secure password reset with 256-bit encryption</p>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
